// Server-side sync for the shared planning rules.
//
// Kept in a SEPARATE module from schedulingRules.js so that the pure
// scheduling logic (schedulingRules.js -> scheduleAlgorithm.js, exercised by
// unit tests) never imports MSAL/authConfig. Only the UI imports this file.
//
// localStorage (in schedulingRules.js) is an instant per-browser cache; this
// endpoint holds the single org-wide rule set shared by every schedule manager
// and persisted across devices and future calendar runs.

import { msalInstance } from '../authConfig';

const API_BASE = '/api/planning-rules';
let sharedRulesEtag = null;
let lastSyncError = null;
const GRAPH_SCOPES = [
  'https://graph.microsoft.com/User.Read',
  'https://graph.microsoft.com/GroupMember.Read.All'
];

const getGraphToken = async () => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) throw new Error('Not signed in.');
  const request = { scopes: GRAPH_SCOPES, account };
  let result;
  try {
    result = await msalInstance.acquireTokenSilent(request);
  } catch (e) {
    result = await msalInstance.acquireTokenPopup(request);
  }
  return result.accessToken;
};

const normalizeRules = (parsed) => ({
  excludeKeywords: Array.isArray(parsed?.excludeKeywords) ? parsed.excludeKeywords : [],
  excludedCourseKeys: Array.isArray(parsed?.excludedCourseKeys) ? parsed.excludedCourseKeys : [],
  courseProfileOptions: Array.isArray(parsed?.courseProfileOptions) ? parsed.courseProfileOptions : [],
  planningNotes: Array.isArray(parsed?.planningNotes) ? parsed.planningNotes : [],
  schedulingRules: Array.isArray(parsed?.schedulingRules) ? parsed.schedulingRules : []
});

// Fetch the shared rule set from the server. The caller decides whether to
// accept it and update localStorage so a slow initial read cannot overwrite a
// rule the user edited while the request was in flight.
export const fetchSharedPlanningRules = async () => {
  try {
    const token = await getGraphToken();
    const res = await fetch(API_BASE, {
      headers: { 'x-graph-access-token': token, Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error(`GET planning-rules -> ${res.status}`);
    const body = await res.json();
    lastSyncError = null;
    return {
      rules: normalizeRules(body),
      etag: res.headers?.get?.('etag') || body?._etag || null
    };
  } catch (e) {
    lastSyncError = 'error';
    console.warn('Could not load shared planning rules (using local cache):', e.message);
    return null;
  }
};

// Adopt an ETag only when the UI also accepts the matching rule snapshot. If a
// user edited while a GET was in flight, adopting that GET's ETag would allow
// the older local snapshot to overwrite the newer shared record.
export const acceptSharedPlanningRulesSnapshot = (etag) => {
  sharedRulesEtag = etag || null;
};

let saveQueue = Promise.resolve(true);

const writeSharedPlanningRules = async (rules) => {
  try {
    const token = await getGraphToken();
    const res = await fetch(API_BASE, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-graph-access-token': token,
        Authorization: `Bearer ${token}`,
        ...(sharedRulesEtag ? { 'If-Match': sharedRulesEtag } : {})
      },
      body: JSON.stringify(normalizeRules(rules))
    });
    if (res.status === 409 || res.status === 412 || res.status === 428) {
      lastSyncError = 'conflict';
      return false;
    }
    if (!res.ok) throw new Error(`PUT planning-rules -> ${res.status}`);
    sharedRulesEtag = res.headers?.get?.('etag') || sharedRulesEtag;
    lastSyncError = null;
    return true;
  } catch (e) {
    lastSyncError = 'error';
    console.warn('Could not save shared planning rules to server:', e.message);
    return false;
  }
};

// Serialize whole-record writes. Include/exclude toggles and rule edits can
// happen rapidly; concurrent PUTs may otherwise finish out of order and leave
// an older rule set as the organization-wide value.
export const pushSharedPlanningRules = (rules) => {
  const snapshot = normalizeRules(rules);
  saveQueue = saveQueue.then(
    () => writeSharedPlanningRules(snapshot),
    () => writeSharedPlanningRules(snapshot)
  );
  return saveQueue;
};

export const getPlanningRulesSyncError = () => lastSyncError;
